import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import ListingCard from "@/components/ListingCard";

/**
 * SEO landing page: /professionisti-affitti-brevi/{citta}
 * Ultra-targeted long-tail keyword page. One per top Italian city (30 total).
 * Auto-generated meta, JSON-LD, dynamic content per city.
 */
export default function CityLanding() {
  const { city: citySlug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/seo/city/${citySlug}`)
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, [citySlug]);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="font-heading font-black text-3xl">Città non trovata</h1>
        <button onClick={() => nav("/")} className="mt-6 px-4 py-2 rounded bg-[#FFBE00] text-black font-bold uppercase text-xs">← Home</button>
      </div>
    );
  }
  if (!data) return <div className="text-center py-20 text-zinc-400">Caricamento…</div>;

  const { city, total_listings: total, by_category, categories } = data;
  const title = `Professionisti affitti brevi a ${city.name} | HostMarket`;
  const description = `Trova i migliori professionisti per affitti brevi a ${city.name}: fotografi, home stager, property manager, commercialisti, avvocati specializzati. ${total} annunci verificati.`;
  const url = `${window.location.origin}/professionisti-affitti-brevi/${citySlug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: title,
    description,
    url,
    about: { "@type": "City", name: city.name, containedInPlace: { "@type": "AdministrativeArea", name: city.region } },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin },
        { "@type": "ListItem", position: 2, name: city.name, item: url },
      ],
    },
  };

  return (
    <div data-testid="city-landing" className="bg-black">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={`affitti brevi ${city.name}, professionisti affitti brevi ${city.name}, property manager ${city.name}, fotografo affitti brevi ${city.name}, home stager ${city.name}, short term rental ${city.name}, airbnb ${city.name}`} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* HERO */}
      <section className="border-b border-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: "radial-gradient(1200px 400px at 50% 20%, rgba(255,190,0,0.12), transparent 60%)" }}></div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="text-xs uppercase tracking-[0.25em] text-[#FFBE00] mb-3">· {city.region} · Affitti brevi</div>
          <h1 className="font-heading font-black text-3xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-4xl">
            Professionisti affitti brevi<br />
            a <span className="text-[#FFBE00]">{city.name}</span>.
          </h1>
          <p className="mt-5 max-w-2xl text-zinc-300 text-base md:text-lg">{city.intro}</p>
          <p className="mt-3 max-w-2xl text-sm text-zinc-400">
            <strong className="text-white">{total} annunci</strong> di professionisti verificati · host, property manager e B&B di {city.name} li usano ogni giorno.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/annunci" className="px-6 py-3 rounded-md bg-[#FFBE00] text-black font-bold uppercase tracking-wider text-sm hover:bg-[#E619A8] hover:text-white">Sfoglia tutti →</Link>
            <Link to="/registrati" className="px-6 py-3 rounded-md border border-zinc-700 text-white font-bold uppercase tracking-wider text-sm hover:border-[#FFBE00] hover:text-[#FFBE00]">Pubblica il tuo annuncio</Link>
          </div>
        </div>
      </section>

      {/* CATEGORY QUICK LINKS */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <h2 className="font-heading font-black text-2xl md:text-3xl mb-5">Categorie professionali a {city.name}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {categories.map((c) => (
            <Link key={c.slug} to={`/${c.slug}/${citySlug}`} className="hm-card p-4 hover:border-[#FFBE00]" data-testid={`city-cat-${c.slug}`}>
              <div className="font-bold text-sm">{c.name}</div>
              <div className="text-xs text-zinc-500 mt-1">{(by_category[c.slug] || []).length} a {city.name}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* LISTINGS BY CATEGORY */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-10">
        {categories.filter((c) => (by_category[c.slug] || []).length > 0).map((c) => (
          <div key={c.slug}>
            <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
              <h2 className="font-heading font-black text-xl md:text-2xl">
                <span className="text-[#FFBE00]">{c.name}</span> a {city.name}
              </h2>
              <Link to={`/${c.slug}/${citySlug}`} className="text-xs uppercase tracking-wider text-zinc-400 hover:text-[#FFBE00]">Tutti →</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {(by_category[c.slug] || []).slice(0, 4).map((l) => <ListingCard key={l.id} listing={l} />)}
            </div>
          </div>
        ))}
        {total === 0 && (
          <div className="hm-card p-10 text-center">
            <h2 className="font-heading font-bold text-xl">Sei un professionista a {city.name}?</h2>
            <p className="text-sm text-zinc-400 mt-2">Al momento non ci sono ancora annunci a {city.name}. Sii il primo a pubblicare!</p>
            <Link to="/registrati" className="mt-5 inline-block px-6 py-3 rounded-md bg-[#FFBE00] text-black font-bold uppercase text-xs">Pubblica ora →</Link>
          </div>
        )}
      </section>
    </div>
  );
}
